"use client";
import React, { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function OfferSlider({
  children,
}: {
  children: React.ReactNode;
}) {
  const items = React.Children.toArray(children);
  const total = items.length;

  const [page, setPage] = useState(0);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Mobile: 1 card at a time, Desktop: 4 per page
  const PER_PAGE = isMobile ? 1 : 4;
  const totalPages = Math.ceil(total / PER_PAGE);

  const prev = () => setPage((p) => (p - 1 + totalPages) % totalPages);
  const next = () => setPage((p) => (p + 1) % totalPages);

  // Reset to page 0 when PER_PAGE changes to avoid out-of-range
  React.useEffect(() => {
    setPage(0);
  }, [PER_PAGE]);

  const start = page * PER_PAGE;
  const visible = items.slice(start, start + PER_PAGE);

  return (
    <div className="relative">
      {/* Left Button — always on mobile, only when >4 cards on desktop */}
      {(isMobile || total > 4) && (
      <button
        onClick={prev}
        className="absolute left-[-16px] sm:left-[-50px] top-1/2 -translate-y-1/2 z-40
                   p-2 sm:p-4 rounded-full
                   bg-white border border-[#800000] text-[#800000]
                   hover:bg-[#800000] hover:text-white
                   transition-all duration-300 shadow-lg flex
                   items-center justify-center active:scale-90 group"
        aria-label="Previous"
      >
        <ArrowLeft size={16} strokeWidth={2.5} className="sm:w-6 sm:h-6 transition-transform group-hover:-translate-x-1" />
      </button>
      )}

      {/* Right Button — always on mobile, only when >4 cards on desktop */}
      {(isMobile || total > 4) && (
      <button
        onClick={next}
        className="absolute right-[-16px] sm:right-[-50px] top-1/2 -translate-y-1/2 z-40
                   p-2 sm:p-4 rounded-full
                   bg-white border border-[#800000] text-[#800000]
                   hover:bg-[#800000] hover:text-white
                   transition-all duration-300 shadow-lg flex
                   items-center justify-center active:scale-90 group"
        aria-label="Next"
      >
        <ArrowRight size={16} strokeWidth={2.5} className="sm:w-6 sm:h-6 transition-transform group-hover:translate-x-1" />
      </button>
      )}

      {/* Cards grid */}
      <div
        className={`grid py-6 px-8 sm:px-0 justify-items-center
          ${isMobile
            ? "grid-cols-1 max-w-sm mx-auto gap-3"
            : `gap-6 ${
                visible.length === 1 ? "grid-cols-1 max-w-xs mx-auto" :
                visible.length === 2 ? "grid-cols-2" :
                visible.length === 3 ? "grid-cols-3" :
                "grid-cols-4"
              }`
          }
        `}
      >
        {visible.map((child, i) => (
          <div key={start + i} className="w-full animate-in fade-in slide-in-from-bottom-2 duration-300">
            {child}
          </div>
        ))}
      </div>

      {/* Page dots */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              className={`rounded-full transition-all duration-300 ${
                i === page
                  ? "w-5 h-2 bg-[#800000]"
                  : "w-2 h-2 bg-[#800000]/20 hover:bg-[#800000]/50"
              }`}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
